import { useState } from 'react';
import type { GameState, Press, BetType, HoleInfo } from '@/lib/golfEngine';
import { getRunningStatusAtEachHole, getLastPlayedHole, getFrontHoles, getBackHoles, getPlayingOrder, calculateBetStatusForHoles, getCompletedHolesInOrder } from '@/lib/golfEngine';
import { getGameEngine, isPressCapable } from '@/games/index';
import { computeConsolidatedPressGroups, type ConsolidatedGroup } from '@/lib/pressGroups';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { DT, dtStatusColor, dtStatusBg } from '@/lib/darkTheme';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface PressRowsProps {
  gameState: GameState;
  onAddPress: (betType: BetType, side: 'top' | 'bottom', count: number, holeOverride?: number) => void;
  onPressExistingPress: (pressId: string, side: 'top' | 'bottom') => void;
  selectedHole: number | null;
  readOnly?: boolean;
}

function formatCellStatus(status: number, stackCount?: number): string {
  const multiplier = stackCount && stackCount > 1 ? ` x${stackCount}` : '';
  if (status === 0) return `E${multiplier}`;
  if (status > 0) return `+${status}${multiplier}`;
  return `${status}${multiplier}`;
}

interface BetRowProps {
  label: string;
  holes: HoleInfo[];
  statusMap: Map<number, number>;
  finalStatus: number;
  gridCols: string;
  stackCount?: number;
}

function HoleHeaderRow({ holes, gridCols }: { holes: HoleInfo[]; gridCols: string }) {
  return (
    <div className={cn("grid", gridCols)} style={{ borderBottom: `1px solid ${DT.divider}` }}>
      <div className="flex items-center px-1 h-6" style={{ fontSize: '12px', fontWeight: 700, color: DT.primaryText, background: DT.brandEmerald, borderRight: `1px solid ${DT.divider}`, position: 'sticky', left: 0, zIndex: 10 }}>
        HOLE
      </div>
      {holes.map((hole) => (
        <div
          key={hole.number}
          className="flex items-center justify-center h-6"
          style={{ fontSize: '12px', fontWeight: 700, color: DT.gold, background: DT.brandEmerald, borderRight: `1px solid ${DT.divider}` }}
        >
          {hole.number}
        </div>
      ))}
      <div className="flex items-center justify-center h-6" style={{ fontSize: '12px', fontWeight: 700, color: DT.primaryText, background: DT.brandEmerald }}>
        TOT
      </div>
    </div>
  );
}

function BetRow({ label, holes, statusMap, finalStatus, gridCols, stackCount }: BetRowProps) {
  return (
    <div className={cn("grid", gridCols)} style={{ borderBottom: `1px solid ${DT.divider}` }}>
      <div className="flex items-center px-1 h-8" style={{ fontSize: '12px', fontWeight: 700, color: DT.primaryText, background: DT.darkBg, borderRight: `1px solid ${DT.divider}`, position: 'sticky', left: 0, zIndex: 10 }}>
        {label}
      </div>
      {holes.map((hole) => {
        const status = statusMap.get(hole.number);
        const hasStatus = status !== undefined;
        
        return (
          <div
            key={hole.number}
            className="flex items-center justify-center h-8"
            style={{
              fontSize: '12px', fontFamily: 'monospace', fontWeight: 700,
              color: hasStatus ? dtStatusColor(status) : DT.primaryText,
              opacity: hasStatus ? 1 : 0.2,
              background: hasStatus ? dtStatusBg(status) : DT.darkBg,
              borderRight: `1px solid ${DT.divider}`,
            }}
          >
            {hasStatus ? formatCellStatus(status, stackCount) : '-'}
          </div>
        );
      })}
      <div
        className="flex items-center justify-center h-8"
        style={{
          fontFamily: 'monospace', fontWeight: 800, fontSize: '12px',
          color: dtStatusColor(finalStatus),
          background: dtStatusBg(finalStatus),
        }}
      >
        {formatCellStatus(finalStatus, stackCount)}
      </div>
    </div>
  );
}

interface OverallRowProps {
  gameState: GameState;
  frontNine: HoleInfo[];
  backNine: HoleInfo[];
  gridCols: string;
}

function OverallRow({ gameState, frontNine, backNine, gridCols }: OverallRowProps) {
  const playingOrder = getPlayingOrder(gameState.startingHole);
  const completedHoles = getCompletedHolesInOrder(gameState);
  
  const topPress = gameState.presses.find(p => p.betType === 'Overall' && p.initiatedBy === 'top');
  const bottomPress = gameState.presses.find(p => p.betType === 'Overall' && p.initiatedBy === 'bottom');
  
  const calculateHoleResult = (holeNum: number): number => {
    const topTeam = gameState.teams[0];
    const bottomTeam = gameState.teams[1];
    
    const getPlayerNet = (player: typeof topTeam.players[0]): number | null => {
      const score = gameState.scores.find(s => s.playerId === player.id && s.holeNumber === holeNum);
      if (!score || score.netScore === null) return null;
      return score.netScore;
    };
    
    const topNets = topTeam.players.map(getPlayerNet).filter((n): n is number => n !== null);
    const bottomNets = bottomTeam.players.map(getPlayerNet).filter((n): n is number => n !== null);
    
    if (topNets.length === 0 || bottomNets.length === 0) return 0;
    
    const topBest = Math.min(...topNets);
    const bottomBest = Math.min(...bottomNets);
    
    if (topBest < bottomBest) return 1;
    if (bottomBest < topBest) return -1;
    return 0;
  };
  
  const buildOverallStatusMap = () => {
    const statusMap = new Map<number, number>();
    let runningTotal = 0;
    
    for (const holeNum of playingOrder) {
      if (!completedHoles.includes(holeNum)) break;
      const holeResult = calculateHoleResult(holeNum);
      runningTotal += holeResult;
      statusMap.set(holeNum, runningTotal);
    }
    return statusMap;
  };
  
  const overallStatusMap = buildOverallStatusMap();
  const overallFinal = Array.from(overallStatusMap.values()).pop() ?? 0;
  
  const buildPressStatusMap = (pressStartHole: number | null, betHoles?: number[]) => {
    if (!pressStartHole) return new Map<number, number>();
    
    const pressStatusMap = new Map<number, number>();
    
    const pressStartPos = playingOrder.indexOf(pressStartHole);
    
    const relevantHoles = betHoles || playingOrder;
    const lastRelevantPos = Math.max(...relevantHoles.map(h => playingOrder.indexOf(h)));
    const isLastHolePress = pressStartPos === lastRelevantPos;
    
    if (!isLastHolePress) {
      pressStatusMap.set(pressStartHole, 0);
    }
    
    let runningPressTotal = 0;
    
    for (const holeNum of playingOrder) {
      const holePos = playingOrder.indexOf(holeNum);
      if (isLastHolePress) {
        if (holePos < pressStartPos) continue;
      } else {
        if (holePos <= pressStartPos) continue;
      }
      if (!completedHoles.includes(holeNum)) break;
      
      const holeResult = calculateHoleResult(holeNum);
      runningPressTotal += holeResult;
      pressStatusMap.set(holeNum, runningPressTotal);
    }
    
    return pressStatusMap;
  };
  
  const renderOverallBaseRow = (holes: HoleInfo[], label: string, sectionFinal: number) => (
    <div className={cn("grid", gridCols)} style={{ borderBottom: `1px solid ${DT.divider}` }}>
      <div className="flex items-center px-1 h-8" style={{ fontSize: '12px', fontWeight: 700, color: DT.primaryText, background: DT.darkBg, borderRight: `1px solid ${DT.divider}`, position: 'sticky', left: 0, zIndex: 10 }}>
        {label}
      </div>
      {holes.map((hole) => {
        const status = overallStatusMap.get(hole.number);
        const hasStatus = status !== undefined;
        
        return (
          <div
            key={hole.number}
            className="flex items-center justify-center h-8"
            style={{
              fontSize: '12px', fontFamily: 'monospace', fontWeight: 700,
              color: hasStatus ? dtStatusColor(status) : DT.primaryText,
              opacity: hasStatus ? 1 : 0.2,
              background: hasStatus ? dtStatusBg(status) : DT.darkBg,
              borderRight: `1px solid ${DT.divider}`,
            }}
          >
            {hasStatus ? formatCellStatus(status) : '-'}
          </div>
        );
      })}
      <div
        className="flex items-center justify-center h-8"
        style={{
          fontFamily: 'monospace', fontWeight: 800, fontSize: '12px',
          color: dtStatusColor(sectionFinal),
          background: dtStatusBg(sectionFinal),
        }}
      >
        {formatCellStatus(sectionFinal)}
      </div>
    </div>
  );
  
  // Format the "oP" cell display with running status from initiator's perspective
  const formatOPStatus = (status: number, stackCount: number): string => {
    const multiplier = stackCount > 1 ? ` x${stackCount}` : '';
    if (status === 0) return `oP${multiplier}`;
    if (status > 0) return `oP +${status}${multiplier}`;
    return `oP ${status}${multiplier}`;
  };
  
  // Render a single section (front or back) of an Overall press row
  const renderOverallPressSection = (
    press: Press, 
    sectionHoles: HoleInfo[], 
    isTop: boolean, 
    sectionFinal: number,
    sectionName: 'front' | 'back'
  ) => {
    const pressStartHole = press.startingHole;
    const pressStartPos = playingOrder.indexOf(pressStartHole);
    const pressStatusMap = buildPressStatusMap(pressStartHole, playingOrder);
    
    const teamBg = isTop ? DT.team1Bg : DT.team2Bg;
    const teamLabel = isTop ? 'A oP' : 'B oP';
    const stackCount = press.stackCount;
    
    return (
      <div key={`${press.id}-${sectionName}`} className={cn("grid", gridCols)} style={{ borderBottom: `1px solid ${DT.divider}` }}>
        <div className="flex items-center px-1 h-8" style={{ fontSize: '12px', fontWeight: 700, color: DT.primaryText, background: isTop ? DT.team1StickyBg : DT.team2StickyBg, borderRight: `1px solid ${DT.divider}`, position: 'sticky', left: 0, zIndex: 10 }}>
          {teamLabel}
        </div>
        {sectionHoles.map((hole) => {
          const holePos = playingOrder.indexOf(hole.number);
          const isPressStart = pressStartHole === hole.number;
          const rawPressStatus = pressStatusMap.get(hole.number);
          const hasPressStatus = rawPressStatus !== undefined;
          
          const pressStatus = hasPressStatus ? (isTop ? rawPressStatus : -rawPressStatus) : undefined;
          
          if (holePos < pressStartPos) {
            return (
              <div
                key={hole.number}
                className="flex items-center justify-center h-8"
                style={{ fontSize: '12px', fontFamily: 'monospace', color: DT.primaryText, opacity: 0.2, background: DT.darkBg, borderRight: `1px solid ${DT.divider}` }}
              >
                -
              </div>
            );
          }
          
          if (isPressStart || hasPressStatus) {
            const displayStatus = pressStatus ?? 0;
            return (
              <div
                key={hole.number}
                className="flex items-center justify-center h-8"
                style={{ fontSize: '12px', fontFamily: 'monospace', fontWeight: 800, color: DT.primaryText, background: teamBg, borderRight: `1px solid ${DT.divider}` }}
                data-testid={isPressStart ? `overall-press-start-${isTop ? 'top' : 'bottom'}-${hole.number}` : undefined}
              >
                {formatOPStatus(displayStatus, stackCount)}
              </div>
            );
          }
          
          return (
            <div
              key={hole.number}
              className="flex items-center justify-center h-8"
              style={{ fontSize: '12px', fontFamily: 'monospace', color: DT.primaryText, opacity: 0.2, background: DT.darkBg, borderRight: `1px solid ${DT.divider}` }}
            >
              -
            </div>
          );
        })}
        <div
          className="flex items-center justify-center h-8"
          style={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '12px', color: DT.primaryText, background: teamBg }}
        >
          {formatOPStatus(sectionFinal, stackCount)}
        </div>
      </div>
    );
  };
  
  // Calculate the press final status at end of a section (for TOT column)
  const getPressStatusAtEndOfSection = (press: Press, sectionHoles: HoleInfo[], isTop: boolean): number => {
    const betHoles = press.betType === 'Front' ? getFrontHoles(gameState.startingHole) :
                     press.betType === 'Back' ? getBackHoles(gameState.startingHole) :
                     playingOrder.map(h => h);
    const pressStatusMap = buildPressStatusMap(press.startingHole, betHoles);
    // Get the last status in this section
    let lastStatus = 0;
    for (const hole of sectionHoles) {
      const status = pressStatusMap.get(hole.number);
      if (status !== undefined) {
        lastStatus = status;
      }
    }
    // Convert to initiator's perspective
    return isTop ? lastStatus : -lastStatus;
  };
  
  // Calculate front section final based on completed holes in front bet
  const frontHoleNumbers = getFrontHoles(gameState.startingHole);
  const backHoleNumbers = getBackHoles(gameState.startingHole);
  const frontBetFinal = frontHoleNumbers
    .filter(h => completedHoles.includes(h))
    .reduce((total, h) => total + calculateHoleResult(h), 0);
  const allHoles = [...frontNine, ...backNine];
  
  const renderHoleHeader = (holes: HoleInfo[]) => (
    <div className={cn("grid", gridCols)} style={{ borderBottom: `1px solid ${DT.divider}` }}>
      <div className="flex items-center px-1 h-6" style={{ fontSize: '12px', fontWeight: 700, color: DT.primaryText, background: DT.brandEmerald, borderRight: `1px solid ${DT.divider}`, position: 'sticky', left: 0, zIndex: 10 }}>
        HOLE
      </div>
      {holes.map((hole) => (
        <div
          key={hole.number}
          className="flex items-center justify-center h-6"
          style={{ fontSize: '12px', fontWeight: 700, color: DT.gold, background: DT.brandEmerald, borderRight: `1px solid ${DT.divider}` }}
        >
          {hole.number}
        </div>
      ))}
      <div className="flex items-center justify-center h-6" style={{ fontSize: '12px', fontWeight: 700, color: DT.primaryText, background: DT.brandEmerald }}>
        TOT
      </div>
    </div>
  );
  
  // In Two Down Auto mode, Overall is a single fixed bet with no presses
  // Only show press rows in On-Demand Press mode
  const showOverallPresses = gameState.gameMode === 'OnDemandPress';
  
  // Calculate section finals for press rows
  const topPressFrontFinal = topPress ? getPressStatusAtEndOfSection(topPress, frontNine, true) : 0;
  const topPressOverallFinal = topPress ? getPressStatusAtEndOfSection(topPress, [...frontNine, ...backNine], true) : 0;
  const bottomPressFrontFinal = bottomPress ? getPressStatusAtEndOfSection(bottomPress, frontNine, false) : 0;
  const bottomPressOverallFinal = bottomPress ? getPressStatusAtEndOfSection(bottomPress, [...frontNine, ...backNine], false) : 0;
  
  const frontHoleSet = new Set(frontHoleNumbers);
  const backHoleSet = new Set(backHoleNumbers);
  
  const pressStartsInFront = (press: Press) => frontHoleSet.has(press.startingHole);
  const pressStartsInBack = (press: Press) => backHoleSet.has(press.startingHole);
  
  return (
    <>
      {/* Front section: Overall base row + press rows that started in front */}
      {renderOverallBaseRow(frontNine, 'OVERALL', frontBetFinal)}
      {showOverallPresses && topPress && pressStartsInFront(topPress) && renderOverallPressSection(topPress, frontNine, true, topPressFrontFinal, 'front')}
      {showOverallPresses && bottomPress && pressStartsInFront(bottomPress) && renderOverallPressSection(bottomPress, frontNine, false, bottomPressFrontFinal, 'front')}
      
      {/* Back section header */}
      {renderHoleHeader(backNine)}
      
      {/* Back section: Overall base row + press rows (those starting in front continue here, those starting in back appear here) */}
      {renderOverallBaseRow(backNine, 'OVERALL', overallFinal)}
      {showOverallPresses && topPress && renderOverallPressSection(topPress, backNine, true, topPressOverallFinal, 'back')}
      {showOverallPresses && bottomPress && renderOverallPressSection(bottomPress, backNine, false, bottomPressOverallFinal, 'back')}
    </>
  );
}

function PressControls({ 
  gameState, 
  onAddPress,
  onPressExistingPress,
  selectedHole
}: { 
  gameState: GameState;
  onAddPress: (betType: BetType, side: 'top' | 'bottom', count: number, holeOverride?: number) => void;
  onPressExistingPress: (pressId: string, side: 'top' | 'bottom') => void;
  selectedHole: number | null;
}) {
  const engine = getGameEngine(gameState.gameType);
  const pressEng = isPressCapable(engine) ? engine : null;

  // Only show in On-Demand Press mode
  if (gameState.gameMode !== 'OnDemandPress' || !pressEng) return null;

  // Single source of truth for what's pressable right now (shared with the
  // per-hole carousel in HoleDetailModal.tsx) so both surfaces always agree
  // and never collapse two distinct bet types into one ambiguous action.
  const consolidatedGroups = computeConsolidatedPressGroups(gameState, selectedHole);

  const hasAvailableActions = consolidatedGroups.length > 0;
  
  // Check if both Front AND Back have available presses (for "Press ?" indicator)
  const hasFrontPress = consolidatedGroups.some(g => g.betType === 'Front');
  const hasBackPress = consolidatedGroups.some(g => g.betType === 'Back');
  const hasBothFrontAndBack = hasFrontPress && hasBackPress;

  // Helper to render a consolidated group
  const renderConsolidatedGroup = (group: ConsolidatedGroup) => {
    const { betType, side, mainPressMax, pressOnPressOptions, holeOverride } = group;
    const totalOptions = mainPressMax + pressOnPressOptions.length;
    
    const mainLabel = betType === 'Front' ? 'Front Press' : betType === 'Back' ? 'Back Press' : 'Overall Press';
    
    if (totalOptions === 1) {
      if (mainPressMax === 1) {
        return (
          <Button
            key={`${betType}-${side}`}
            size="sm"
            variant="outline"
            className="h-9 px-3 text-sm font-semibold"
            style={{ background: DT.darkBg, borderColor: DT.interactive, color: DT.interactive }}
            onClick={() => onAddPress(betType, side, 1, holeOverride)}
            data-testid={`press-btn-${betType.toLowerCase()}-${side}`}
          >
            <Plus className="w-3 h-3 mr-1" />
            {mainLabel}
          </Button>
        );
      } else {
        const pop = pressOnPressOptions[0];
        return (
          <Button
            key={`press-${pop.pressId}-${side}`}
            size="sm"
            variant="outline"
            className="h-9 px-3 text-sm font-semibold"
            style={{ background: DT.team2Bg, borderColor: DT.interactive, color: DT.interactive }}
            onClick={() => onPressExistingPress(pop.pressId, side)}
            data-testid={`press-btn-existing-${pop.pressId}-${side}`}
          >
            <Plus className="w-3 h-3 mr-1" />
            {pop.label}
          </Button>
        );
      }
    }
    
    const betLabel = betType === 'Front' ? 'F' : betType === 'Back' ? 'B' : 'Over';
    
    return (
      <DropdownMenu key={`${betType}-${side}`}>
        <DropdownMenuTrigger asChild>
          <Button
            size="sm"
            variant="outline"
            className="h-9 px-3 text-sm font-semibold"
            style={{ background: DT.darkBg, borderColor: DT.interactive, color: DT.interactive }}
            data-testid={`press-btn-${betType.toLowerCase()}-${side}`}
          >
            <Plus className="w-3 h-3 mr-1" />
            {betLabel} Press (1-{totalOptions})
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" sideOffset={4}>
          {/* Main bet press options: Press 1x, Press 2x, etc. */}
          {mainPressMax > 0 && Array.from({ length: mainPressMax }, (_, i) => i + 1).map(count => (
            <DropdownMenuItem
              key={`main-${count}`}
              onClick={() => onAddPress(betType, side, count, holeOverride)}
              data-testid={`press-option-${betType.toLowerCase()}-${side}-${count}`}
            >
              Press {count}x
            </DropdownMenuItem>
          ))}
          {/* Press-on-press options: P1 F, P2 F, etc. */}
          {pressOnPressOptions.map(pop => (
            <DropdownMenuItem
              key={`pop-${pop.pressId}`}
              onClick={() => onPressExistingPress(pop.pressId, side)}
              data-testid={`press-option-pop-${pop.pressId}-${side}`}
              style={{ color: DT.interactive }}
            >
              {pop.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <div className="shadow-sm" style={{ background: DT.darkBg, borderTop: `2px solid ${DT.interactive}` }} data-testid="presses-section">
      <div className="px-3 py-1.5 flex items-center gap-2 flex-wrap" style={{ background: DT.brandEmerald, borderBottom: `1px solid ${DT.divider}` }}>
        <span className="text-sm font-bold uppercase tracking-wide" style={{ color: DT.interactive }}>Presses</span>
        <span className="text-xs italic font-bold" style={{ color: DT.primaryText }} data-testid="text-scoring-reference">- Scoring is based on Team/Player 1 sitting on top of the card.</span>
        {hasBothFrontAndBack && (
          <span className="text-xs px-2 py-0.5 rounded font-medium" style={{ background: `${DT.interactive}22`, color: DT.interactive, border: `1px solid ${DT.interactive}44` }}>
            Press ?
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 px-3 py-2 flex-wrap min-h-[44px]">
        {hasAvailableActions ? (
          <>
            {consolidatedGroups.map(group => renderConsolidatedGroup(group))}
          </>
        ) : (
          <span className="text-xs italic" style={{ color: DT.primaryText, opacity: 0.4 }}>No presses available yet - play some holes first</span>
        )}
      </div>
    </div>
  );
}

export function PressRows({ gameState, onAddPress, onPressExistingPress, selectedHole, readOnly }: PressRowsProps) {
  const frontHoleNumbers = getFrontHoles(gameState.startingHole);
  const backHoleNumbers = getBackHoles(gameState.startingHole);
  
  // Get the HoleInfo objects in the correct order
  const frontNine = frontHoleNumbers.map(num => gameState.holes.find(h => h.number === num)!).filter(Boolean);
  const backNine = backHoleNumbers.map(num => gameState.holes.find(h => h.number === num)!).filter(Boolean);
  const playingOrder = getPlayingOrder(gameState.startingHole);
  
  const gridCols = "grid-cols-[80px_repeat(9,44px)_40px]";

  // Calculate running status for each bet using playing order
  const completedHoles = getCompletedHolesInOrder(gameState);
  
  const buildRunningStatusMap = (holes: number[]): Map<number, number> => {
    const statusMap = new Map<number, number>();
    let runningTotal = 0;
    
    for (const holeNum of holes) {
      if (!completedHoles.includes(holeNum)) break;
      const holeResult = calculateHoleResult(gameState, holeNum);
      runningTotal += holeResult;
      statusMap.set(holeNum, runningTotal);
    }
    return statusMap;
  };
  
  const frontStatus = buildRunningStatusMap(frontHoleNumbers);
  const backStatus = buildRunningStatusMap(backHoleNumbers);
  
  const frontFinal = Array.from(frontStatus.values()).pop() ?? 0;
  const backFinal = Array.from(backStatus.values()).pop() ?? 0;

  const frontPresses = gameState.presses.filter(p => p.betType === 'Front');
  const backPresses = gameState.presses.filter(p => p.betType === 'Back');

  const renderPressRow = (press: Press, holes: HoleInfo[], betHoleNumbers: number[], pressNumber: number) => {
    const statusMap = new Map<number, number>();
    const pressStartPos = playingOrder.indexOf(press.startingHole);
    
    const lastBetHolePos = Math.max(...betHoleNumbers.map(h => playingOrder.indexOf(h)));
    const isLastHolePress = pressStartPos === lastBetHolePos;
    
    if (!isLastHolePress) {
      statusMap.set(press.startingHole, 0);
    }
    
    let runningTotal = 0;
    for (const holeNum of betHoleNumbers) {
      const holePos = playingOrder.indexOf(holeNum);
      if (isLastHolePress) {
        if (holePos < pressStartPos) continue;
      } else {
        if (holePos <= pressStartPos) continue;
      }
      if (!completedHoles.includes(holeNum)) break;
      
      const holeResult = calculateHoleResult(gameState, holeNum);
      runningTotal += holeResult;
      statusMap.set(holeNum, runningTotal);
    }
    
    const finalStatus = runningTotal;
    const label = `P${pressNumber}`;
    
    return (
      <BetRow
        key={press.id}
        label={label}
        holes={holes}
        statusMap={statusMap}
        finalStatus={finalStatus}
        gridCols={gridCols}
        stackCount={press.stackCount}
      />
    );
  };

  // Helper label for which holes are in each bet
  const frontLabel = gameState.startingHole === 1 ? 'Front 9 Bets (1-9)' : 'Front 9 Bets (10-18)';
  const backLabel = gameState.startingHole === 1 ? 'Back 9 Bets (10-18)' : 'Back 9 Bets (1-9)';
  const overallLabel = gameState.startingHole === 1 ? 'Overall (1-18)' : 'Overall (10-18, 1-9)';

  return (
    <div className="rounded-lg shadow-sm mt-2" style={{ background: DT.surface, border: `1px solid ${DT.divider}`, minWidth: 'max-content' }}>
      <div>
        {!readOnly && <PressControls gameState={gameState} onAddPress={onAddPress} onPressExistingPress={onPressExistingPress} selectedHole={selectedHole} />}
          
          <div className="px-2 py-1" style={{ background: DT.brandEmerald, borderBottom: `1px solid ${DT.divider}` }}>
            <span className="text-xs font-bold uppercase tracking-wide" style={{ color: DT.gold }}>{frontLabel}</span>
          </div>
          
          <HoleHeaderRow holes={frontNine} gridCols={gridCols} />
          
          <BetRow
            label="FRONT"
            holes={frontNine}
            statusMap={frontStatus}
            finalStatus={frontFinal}
            gridCols={gridCols}
          />
          
          {frontPresses.map((press, index) => renderPressRow(press, frontNine, frontHoleNumbers, index + 1))}
          
          <div className="h-1" style={{ background: DT.divider }} />
          
          <div className="px-2 py-1" style={{ background: DT.brandEmerald, borderBottom: `1px solid ${DT.divider}` }}>
            <span className="text-xs font-bold uppercase tracking-wide" style={{ color: DT.gold }}>{backLabel}</span>
          </div>
          
          <HoleHeaderRow holes={backNine} gridCols={gridCols} />
          
          <BetRow
            label="BACK"
            holes={backNine}
            statusMap={backStatus}
            finalStatus={backFinal}
            gridCols={gridCols}
          />
          
          {backPresses.map((press, index) => renderPressRow(press, backNine, backHoleNumbers, index + 1))}
          
          <div className="h-1" style={{ background: DT.divider }} />
          
          <div className="px-2 py-1" style={{ background: DT.brandEmerald, borderBottom: `1px solid ${DT.divider}` }}>
            <span className="text-xs font-bold uppercase tracking-wide" style={{ color: DT.gold }}>{overallLabel}</span>
          </div>
          
          <HoleHeaderRow holes={frontNine} gridCols={gridCols} />
          
          <OverallRow
            gameState={gameState}
            frontNine={frontNine}
            backNine={backNine}
            gridCols={gridCols}
          />
      </div>
    </div>
  );
}

function calculateHoleResult(gameState: GameState, holeNum: number): number {
  const topTeam = gameState.teams[0];
  const bottomTeam = gameState.teams[1];
  const hole = gameState.holes.find(h => h.number === holeNum);
  if (!hole) return 0;
  
  const getPlayerNet = (player: typeof topTeam.players[0]): number | null => {
    const score = gameState.scores.find(s => s.playerId === player.id && s.holeNumber === holeNum);
    if (!score || score.netScore === null) return null;
    return score.netScore;
  };
  
  const topNets = topTeam.players.map(getPlayerNet).filter((n): n is number => n !== null);
  const bottomNets = bottomTeam.players.map(getPlayerNet).filter((n): n is number => n !== null);
  
  if (topNets.length === 0 || bottomNets.length === 0) return 0;
  
  const topBest = Math.min(...topNets);
  const bottomBest = Math.min(...bottomNets);
  
  if (topBest < bottomBest) return 1;
  if (bottomBest < topBest) return -1;
  return 0;
}

export default PressRows;
